import { describeGenerationModel } from '~/utils/transforms/predictionModelsDataTransforms';
import { sliceTiltDeg } from '~/utils/obliquity';
import { GenerationModelDetails } from '~/domain/dicom/dicomBase';
import { Modality } from '~/domain/dicom/modality';
import { Image3D } from '~/domain/patient/image3d';
import { formatDate } from '~/utils/date';

export type ImageMetadata = {
    frameOfReferenceUid: string;
    id: string;
    studyUid: string;
    sopClassUid: string;
    modality: Modality;
    rows: number;
    cols: number;
    length: number;
    /** Number of DICOM instances in the series (excludes interpolation). */
    instanceCount: number;
    series: {
        description: string;
        date: Date | undefined;
        id: string;
        number: number | undefined;
    };
    manufacturer: string;
    manufacturerModelName: string;
    imageType: string[];
    modalitySpecificAttributes: {
        kvp?: string;
        ctdi?: string;
        kernel?: string;
        scanningSequence?: string;
        mraAcquisitionType?: string;
        sequenceVariant?: string;
        protocolName?: string;
    };
    pixelSpacing: number[];
    fieldOfViewMm: [number, number, number];
    imageOrientation: number[][];
    patientPosition?: string;
    stationName?: string;
    filterType?: string;
    generationModel?: GenerationModelDetails;
    hasInterpolatedSlices: boolean;
    interpolatedSliceCount: number;
};

/**
 * The name a series is shown under: its description, or `unknownSeries`
 * (the localised "Unknown Series") when it has none. A description is
 * shown as stored, so a series reads the same in every list. The modality
 * is never the name.
 */
export function seriesNameOf(
    description: string | undefined,
    unknownSeries: string
): string {
    return description || unknownSeries;
}

/** The DICOM SeriesNumber of an image, when it has a finite one. */
export function seriesNumberOf(image: Image3D | undefined): number | undefined {
    const seriesNumber = image?.seriesMetadata.seriesNumber;
    return Number.isFinite(seriesNumber) ? seriesNumber : undefined;
}

/**
 * The date an image was acquired: its first slice's acquisition date, else
 * its series date, the first that reads as a DICOM DA ('YYYYMMDD'), raw.
 * Undefined when neither does. PHI: mask wherever rendered.
 */
export function acquisitionDateOf(image: Image3D): string | undefined {
    const firstSlice = image.imageSlices[0];
    return [firstSlice?.acquisitionDate, firstSlice?.seriesDate].find(
        (date) => date !== undefined && formatDate(date) !== undefined
    );
}

/** The AI model that generated an image, as the user reads it. */
export interface GeneratingModel {
    /** That it is AI generated, with the modality mapping when named. */
    readonly generated: string;
    /** The model's label with its version ("Image+ v. 2"). */
    readonly model: string;
    /** The modality mapping the model names ("CBCT → CT"), if it names one. */
    readonly modalityHint?: string;
}

/** How a series is named where it is shown. */
export interface SeriesSummary {
    /** Raw modality string, e.g. 'CT', 'MR', 'PT'. Not PHI. */
    readonly modality: string;
    /**
     * Series description, or the localised "Unknown Series". PHI: mask
     * wherever rendered.
     */
    readonly label: string;
    /** DICOM SeriesNumber. */
    readonly seriesNumber?: number;
    /** The AI model that generated the image; absent for an acquired image. */
    readonly generatedBy?: GeneratingModel;
    /**
     * How many of the image's slices were interpolated to fill missing
     * data, out of how many; absent when every slice was acquired.
     */
    readonly interpolatedSlices?: InterpolatedSliceCount;
    /**
     * How far the image's slices were acquired tilted off the nearest
     * patient axis, in degrees; absent when they lie along one.
     */
    readonly acquisitionTiltDeg?: number;
}

/** How many of an image's slices were interpolated, out of how many. */
export interface InterpolatedSliceCount {
    readonly count: number;
    readonly total: number;
}

/**
 * How a series is named in a row's chrome: its modality code, its name
 * ({@link seriesNameOf}, with `unknownSeries` for no description), its
 * number, the AI model that generated it (undefined for an acquired
 * series), in the words the sidebar uses ({@link describeGenerationModel}),
 * how many of its slices were interpolated
 * (undefined when none was), and how far it was acquired tilted (undefined
 * when it lies along the axes).
 */
export function seriesSummaryOf(
    image: Image3D,
    unknownSeries: string
): SeriesSummary {
    const series = image.seriesMetadata;
    return {
        modality: String(series.modality),
        label: seriesNameOf(series.seriesDescription, unknownSeries),
        seriesNumber: seriesNumberOf(image),
        generatedBy: describeGenerationModel(image.generationModel),
        interpolatedSlices: image.hasInterpolatedSlices
            ? { count: image.interpolatedSliceCount, total: image.length }
            : undefined,
        // The grid decides obliquity, as every tool gate reads it.
        acquisitionTiltDeg: sliceTiltDeg(image.grid),
    };
}

export const transform3DImageData = (image: Image3D): ImageMetadata => {
    const {
        cols,
        rows,
        imageType,
        kvp,
        ctdi,
        kernel,
        scanningSequence,
        mraAcquisitionType,
        sequenceVariant,
        protocolName,
        patientPosition,
        stationName,
        filterType,
    } = image.firstSlice;
    const { seriesDescription, seriesDate, modality } = image.seriesMetadata;

    return {
        frameOfReferenceUid: image.frameOfReferenceUid,
        id: image.seriesUid,
        studyUid: image.studyUid,
        sopClassUid: image.sopClassUid,
        modality: modality ?? Modality.UNKNOWN,
        rows,
        cols,
        length: image.length,
        instanceCount: image.instanceCount,
        series: {
            description: seriesDescription,
            date: seriesDate,
            id: image.seriesUid,
            number: seriesNumberOf(image),
        },
        manufacturer: image.firstSlice.manufacturer,
        manufacturerModelName: image.firstSlice.manufacturerModelName,
        imageType: imageType,
        modalitySpecificAttributes: {
            kvp,
            ctdi,
            kernel,
            scanningSequence,
            mraAcquisitionType,
            sequenceVariant,
            protocolName,
        },
        pixelSpacing: Array.from(image.grid.getRes()),
        fieldOfViewMm: [
            image.grid.sizeXMM,
            image.grid.sizeYMM,
            image.grid.sizeZMM,
        ],
        imageOrientation: [
            Array.from(image.grid.getUnitX()),
            Array.from(image.grid.getUnitY()),
            Array.from(image.grid.getUnitZ()),
        ],
        patientPosition,
        stationName,
        filterType,
        generationModel: image.generationModel,
        hasInterpolatedSlices: image.hasInterpolatedSlices,
        interpolatedSliceCount: image.interpolatedSliceCount,
    };
};
